import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  makeStyles,
  IconButton
} from "@material-ui/core";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt-br";
import "moment/locale/fr";
import "moment/locale/ar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import CloseIcon from "@material-ui/icons/Close";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import EventIcon from "@material-ui/icons/Event";
import Collapse from "@material-ui/core/Collapse";
import PageHelpGuide from "../../components/PageHelpGuide";
import { APPOINTMENTS_HELP_DATA } from "../../constants/pageHelp/appointmentsHelp";
import { getStandardButtonStyles } from "../../styles/standardButtons";
import VideocamIcon from "@material-ui/icons/Videocam";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

const localizer = momentLocalizer(moment);

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
    borderRadius: 16,
    border: `1px solid ${theme.palette.primary.main}40`,
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.45)",
    color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
    overflow: "hidden",
  },
  dialogHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    "& h2": {
      display: "flex",
      alignItems: "center",
      fontSize: "1.15rem",
      fontWeight: 600,
      margin: 0,
      color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
    },
  },
  helpBox: {
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.08)" : "rgba(0, 180, 216, 0.05)",
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.2)" : "rgba(0, 180, 216, 0.15)"
    }`,
    padding: theme.spacing(1.5, 3),
  },
  calendarPaper: {
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    flex: 1,
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    backgroundColor: theme.palette.type === "dark" ? "#2A2D32 !important" : theme.palette.background.paper,
    "& .rbc-calendar": {
      fontFamily: "'Inter', 'Roboto', sans-serif !important",
      color: theme.palette.type === "dark" ? "#E2E8F0 !important" : "inherit",
      flex: 1,
      minHeight: 0,
    },
    "& .rbc-toolbar": {
      marginBottom: theme.spacing(1),
      flexWrap: "wrap",
      gap: theme.spacing(1),
    },
    "& .rbc-toolbar .rbc-toolbar-label": {
      color: theme.palette.type === "dark" ? "#FFFFFF !important" : "#111827 !important",
      fontWeight: 600,
      fontSize: "1.05rem",
      textTransform: "capitalize",
    },
    "& .rbc-toolbar .rbc-btn-group button": {
      color: theme.palette.type === "dark" ? "#E2E8F0 !important" : "#374151 !important",
      border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.15) !important" : "1px solid rgba(0, 0, 0, 0.15) !important",
      backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.05) !important" : "#F3F4F6 !important",
      borderRadius: "6px !important",
      margin: "0 2px",
      padding: "5px 12px",
      fontWeight: 500,
      fontSize: "0.85rem",
      textTransform: "capitalize",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.12) !important" : "#E5E7EB !important",
      },
      "&.rbc-active": {
        backgroundColor: `${theme.palette.primary.main} !important`,
        color: "#FFFFFF !important",
        borderColor: `${theme.palette.primary.main} !important`,
        fontWeight: 600,
      },
    },
    "& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view": {
      border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.1) !important" : "1px solid #E5E7EB !important",
      borderRadius: 12,
      overflow: "hidden",
      backgroundColor: theme.palette.type === "dark" ? "#22252a !important" : "#ffffff !important",
    },
    "& .rbc-header": {
      backgroundColor: theme.palette.type === "dark" ? "#1e2126 !important" : "#F9FAFB !important",
      color: theme.palette.type === "dark" ? "#E2E8F0 !important" : "#374151 !important",
      borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.1) !important" : "1px solid #E5E7EB !important",
      padding: "8px 4px !important",
      fontWeight: 600,
      textTransform: "capitalize",
    },
    "& .rbc-day-bg + .rbc-day-bg, & .rbc-header + .rbc-header": {
      borderLeft: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08) !important" : "1px solid #E5E7EB !important",
    },
    "& .rbc-month-row + .rbc-month-row": {
      borderTop: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08) !important" : "1px solid #E5E7EB !important",
    },
    "& .rbc-off-range-bg": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.32) !important" : "#F3F4F6 !important",
    },
    "& .rbc-today": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(16, 185, 129, 0.15) !important" : "rgba(16, 185, 129, 0.08) !important",
    },
    "& .rbc-date-cell": {
      padding: "4px 8px !important",
      fontWeight: 500,
    },
    "& .rbc-off-range": {
      opacity: "0.35 !important",
    },
  },
  filterControl: {
    minWidth: 260,
  },
  legend: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
    padding: "6px 12px",
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  colorBox: {
    width: 14,
    height: 14,
    borderRadius: 4,
  },
  modalContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  infoBlock: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  btnMeet: {
    marginTop: theme.spacing(1),
    backgroundColor: "#1a73e8",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1557b0",
    }
  }
}));

const Appointments = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [appointments, setAppointments] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [selectedProfessional, setSelectedProfessional] = useState("");
  const [events, setEvents] = useState([]);
  
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    fetchProfessionals();
  }, []);

  useEffect(() => {
    let locale = i18n.language;
    if (locale === "pt") locale = "pt-br";
    moment.locale(locale);
  }, [i18n.language]);

  useEffect(() => {
    fetchAppointments();
  }, [selectedProfessional]);

  const fetchProfessionals = async () => {
    try {
      const { data } = await api.get("/professionals");
      setProfessionals(data);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchAppointments = async () => {
    try {
      const params = {};
      if (selectedProfessional) {
        params.professionalId = selectedProfessional;
      }
      const { data } = await api.get("/appointments", { params });
      setAppointments(data);

      const parsedEvents = data.map(app => {
        const start = new Date(app.date);
        const duration = app.service?.duration || 30;
        const end = moment(start).add(duration, 'minutes').toDate();

        return {
          id: app.id,
          title: `${app.contact?.name || app.contactName || i18n.t("appointments.unnamed")} - ${app.service?.name || i18n.t("appointments.service")}`,
          start,
          end,
          resource: app
        };
      });

      setEvents(parsedEvents);
    } catch (err) {
      toastError(err);
    }
  };

  const eventStyleGetter = (event) => {
    let backgroundColor = '#28a745'; // scheduled
    const status = event.resource.status;

    if (status === 'cancelled') {
      backgroundColor = '#dc3545';
    } else if (status === 'completed') {
      backgroundColor = '#6c757d';
    } else if (status === 'expired') {
      backgroundColor = '#fd7e14';
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
        cursor: 'pointer',
        fontSize: '0.82rem',
        padding: '2px 5px',
      }
    };
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event.resource);
    setModalOpen(true);
  };

  const handleCancelAppointment = async () => {
    if (!window.confirm(i18n.t("appointments.confirmCancel"))) return;

    try {
      await api.delete(`/appointments/${selectedEvent.uuid || selectedEvent.id}`);
      toast.success(i18n.t("appointments.cancelSuccess"));
      setModalOpen(false);
      fetchAppointments();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <Paper className={classes.calendarPaper} variant="outlined">
        <MainHeader>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%", flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <PageHelpGuide
                helpData={APPOINTMENTS_HELP_DATA}
                tooltipText="Guía y Ayuda de Citas (Haz clic para ver)"
              />
              <Title>{i18n.t("appointments.title")}</Title>
            </div>
            
            {user.profile === "admin" && (
              <FormControl variant="outlined" size="small" className={classes.filterControl}>
                <InputLabel>{i18n.t("appointments.filterProfessional")}</InputLabel>
                <Select
                  value={selectedProfessional}
                  onChange={(e) => setSelectedProfessional(e.target.value)}
                  label={i18n.t("appointments.filterProfessional")}
                >
                  <MenuItem value="">{i18n.t("appointments.allProfessionals")}</MenuItem>
                  {professionals.map(prof => (
                    <MenuItem key={prof.id} value={prof.id}>
                      {prof.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>
        </MainHeader>

        <div className={classes.legend}>
          <div className={classes.legendItem}>
            <div className={classes.colorBox} style={{ backgroundColor: '#28a745' }}></div>
            <Typography variant="body2">{i18n.t("appointments.scheduled")}</Typography>
          </div>
          <div className={classes.legendItem}>
            <div className={classes.colorBox} style={{ backgroundColor: '#dc3545' }}></div>
            <Typography variant="body2">{i18n.t("appointments.cancelled")}</Typography>
          </div>
          <div className={classes.legendItem}>
            <div className={classes.colorBox} style={{ backgroundColor: '#6c757d' }}></div>
            <Typography variant="body2">{i18n.t("appointments.completed")}</Typography>
          </div>
          <div className={classes.legendItem}>
            <div className={classes.colorBox} style={{ backgroundColor: '#fd7e14' }}></div>
            <Typography variant="body2">{i18n.t("appointments.expired")}</Typography>
          </div>
        </div>

        <Calendar
          localizer={localizer}
          culture={i18n.language === "pt" ? "pt-br" : i18n.language}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "100%", minHeight: 520 }}
          views={['month', 'week', 'day', 'agenda']}
          defaultView="month"
          popup={true}
          popupOffset={{ x: 0, y: 10 }}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          messages={{
            next: i18n.t("appointments.calNext"),
            previous: i18n.t("appointments.calPrevious"),
            today: i18n.t("appointments.calToday"),
            month: i18n.t("appointments.calMonth"),
            week: i18n.t("appointments.calWeek"),
            day: i18n.t("appointments.calDay"),
            agenda: i18n.t("appointments.calAgenda"),
            showMore: total => i18n.t("appointments.calShowMore").replace('{total}', total),
            noEventsInRange: i18n.t("appointments.calNoEvents")
          }}
        />
      </Paper>

      <Dialog
        open={modalOpen}
        onClose={() => { setModalOpen(false); setShowHelp(false); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: classes.dialogPaper,
        }}
      >
        <div className={classes.dialogHeader}>
          <h2>
            <EventIcon style={{ marginRight: 10, fontSize: 22, color: "#00b4d8" }} />
            {i18n.t("appointments.details")}
          </h2>
          <div style={{ display: "flex", alignItems: "center" }}>
            <IconButton
              size="small"
              onClick={() => setShowHelp((prev) => !prev)}
              title="Ayuda / Información"
              style={{ marginRight: 8, color: showHelp ? "#00b4d8" : "inherit" }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="caption"
              style={{ opacity: 0.6, marginRight: 12, fontWeight: 500 }}
            >
              BotMix | Agendamiento
            </Typography>
            <IconButton size="small" onClick={() => { setModalOpen(false); setShowHelp(false); }} aria-label="Cerrar">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <Collapse in={showHelp}>
          <div className={classes.helpBox}>
            <Typography variant="body2" style={{ color: "#00b4d8", lineHeight: 1.5 }}>
              {i18n.t("appointments.helpSummary") || "Consulta los detalles de la cita agendada, datos del paciente, servicio contratado, profesional asignado y el enlace de videollamada para la sesión."}
            </Typography>
          </div>
        </Collapse>

        <DialogContent dividers className={classes.modalContent}>
          {selectedEvent && (
            <>
              <div className={classes.infoBlock}>
                <Typography variant="subtitle2" color="textSecondary">{i18n.t("appointments.patient")}</Typography>
                <Typography variant="h6">{selectedEvent.contact?.name || selectedEvent.contactName || i18n.t("appointments.unnamed")}</Typography>
                <Typography variant="body2">Tel: {selectedEvent.contact?.number || selectedEvent.contactPhone || i18n.t("appointments.unregistered")}</Typography>
              </div>

              <div className={classes.infoBlock}>
                <Typography variant="subtitle2" color="textSecondary">{i18n.t("appointments.service")}</Typography>
                <Typography variant="body1"><b>{selectedEvent.service?.name}</b> ({selectedEvent.service?.duration} min)</Typography>
                <Typography variant="body1">{i18n.t("appointments.date")}: {moment(selectedEvent.date).format("LLLL")}</Typography>
                <Typography variant="body1">{i18n.t("appointments.professional")}: {selectedEvent.professional?.name}</Typography>
                <Typography variant="body1">{i18n.t("appointments.status")}: <b>{selectedEvent.status === 'scheduled' ? i18n.t("appointments.scheduled") : selectedEvent.status === 'cancelled' ? i18n.t("appointments.cancelled") : selectedEvent.status === 'expired' ? i18n.t("appointments.expired") : i18n.t("appointments.completed")}</b></Typography>
              </div>

              {selectedEvent.professional?.meetingLink && (
                <Button
                  variant="contained"
                  className={classes.btnMeet}
                  startIcon={<VideocamIcon />}
                  href={selectedEvent.professional?.meetingLink}
                  target="_blank"
                >
                  {i18n.t("appointments.joinMeet")}
                </Button>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          {selectedEvent?.status !== "cancelled" && (
            <Button onClick={handleCancelAppointment} className={classes.dangerButton}>
              {i18n.t("appointments.cancelBtn")}
            </Button>
          )}
          <Button onClick={() => { setModalOpen(false); setShowHelp(false); }} className={classes.applyButton}>
            {i18n.t("appointments.closeBtn")}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Appointments;
